/**
 * Gobodo-backed search for Credit Block "New case" modal (assignee + client pickers).
 * Uses UsersApi.searchUsers and GraphObjectsApi.searchGraphObjects via searchGraphObjectsAsRecords.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import type { GenericRecord } from '../recordStorageService';
import { searchGraphObjectsAsRecords } from './gobodoRecordService';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { User } from '../queries/userQueries';
import { getUserFullName } from '../queries/userQueries';

export type CreditBlockSelectOption = { value: string; label: string };

/** Same `User` rows as Admin → Users (`fetchUsersForUserManagement`), formatted for assignee `u:` picks. */
export function usersToCreditBlockAssigneeOptions(users: User[]): CreditBlockSelectOption[] {
  const out: CreditBlockSelectOption[] = [];
  const seen = new Set<string>();
  for (const u of users) {
    const id = String(u.id ?? '').trim();
    if (!id) continue;
    const token = String(u.graphObjectKey ?? id).trim();
    if (!token) continue;
    const value = `u:${token}`;
    if (seen.has(value)) continue;
    seen.add(value);
    const name = getUserFullName(u).trim();
    const label = name || String(u.email ?? '').trim() || String(u.username ?? '').trim() || token;
    out.push({ value, label });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** Small result sets for modal autocompletes; Gobodo also honors cachekey + cacheduration. */
const CB_NEW_CASE_AUTOCOMPLETE_MAX = 15;
/** Gobodo API: cache TTL in seconds for search responses. */
const CB_NEW_CASE_SEARCH_CACHE_DURATION_SEC = 60;
const CB_NEW_CASE_CACHE_GROUP = 'credit_block_new_case';

function userSearchCacheKeys(graphId: number, q: string): string[] {
  return [CB_NEW_CASE_CACHE_GROUP, 'searchUsers', String(graphId), q];
}

function clientSearchCacheKeys(clientTypeKey: string, graphId: number, q: string): string[] {
  return [CB_NEW_CASE_CACHE_GROUP, 'searchGraphObjects', 'Client', clientTypeKey, String(graphId), q];
}

function clientObjectTypeKey(): string {
  return normalizeToGobodoFormat(`Client${GobodoConfig.customSuffix()}`);
}

function graphUserHitsToAssigneeOptions(items: any[]): CreditBlockSelectOption[] {
  const out: CreditBlockSelectOption[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const data = item?.data && typeof item.data === 'object' ? item.data : {};
    const raw = { ...item, ...data };
    const key = String(item?.key ?? raw.key ?? '').trim();
    const id = String(item?.id ?? raw.id ?? '').trim();
    const token = key || id;
    if (!token) continue;
    const first = String(raw.FirstName__c ?? raw.firstName ?? raw.firstname ?? '').trim();
    const last = String(raw.LastName__c ?? raw.lastName ?? raw.lastname ?? '').trim();
    const combined = [first, last].filter(Boolean).join(' ').trim();
    const label =
      combined ||
      String(item?.label ?? raw.label ?? raw.Name__c ?? raw.name ?? raw.DisplayName__c ?? raw.Email__c ?? raw.email ?? token).trim() ||
      token;
    const value = key ? `u:${key}` : `u:${id}`;
    if (seen.has(value)) continue;
    seen.add(value);
    out.push({ value, label });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Assignee / case owner search (graph user search).
 * Empty query uses "*" so opening the dropdown still loads suggestions.
 */
export async function searchCreditBlockAssigneeOptions(query: string): Promise<CreditBlockSelectOption[]> {
  if (!USE_GOBODO_API) return [];
  const graphId = GobodoConfig.graph();
  const q = (query || '').trim() || '*';
  const client = getGobodoClient();
  const usersApi = new GobodoApi.UsersApi(client);
  const rows = await retryWithBackoff(
    () =>
      usersApi.searchUsers(q, {
        max: CB_NEW_CASE_AUTOCOMPLETE_MAX,
        graphid: graphId,
        searchtype: 'graph',
        cachekey: userSearchCacheKeys(graphId, q),
        cacheduration: String(CB_NEW_CASE_SEARCH_CACHE_DURATION_SEC),
        cachegroup: CB_NEW_CASE_CACHE_GROUP,
      }),
    { maxRetries: 2 }
  );
  const list = Array.isArray(rows) ? rows : [];
  return graphUserHitsToAssigneeOptions(list);
}

function genericRecordsToClientSelectOptions(records: GenericRecord[]): CreditBlockSelectOption[] {
  const out: CreditBlockSelectOption[] = [];
  const seen = new Set<string>();
  for (const r of records) {
    const id = String(r.key ?? r.id ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const name = String(r.label ?? r.name ?? r.Name__c ?? r.name ?? id).trim() || id;
    out.push({ value: id, label: name });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Client account search for the modal. Requires a non-empty query (caller gates enabled).
 */
export async function searchCreditBlockClientOptions(query: string): Promise<CreditBlockSelectOption[]> {
  if (!USE_GOBODO_API) return [];
  const q = (query || '').trim();
  if (!q) return [];
  const graphId = GobodoConfig.graph();
  const clientKey = clientObjectTypeKey();
  const records = await searchGraphObjectsAsRecords(q, {
    graphid: graphId,
    max: CB_NEW_CASE_AUTOCOMPLETE_MAX,
    objecttypekey: clientKey,
    cachekey: clientSearchCacheKeys(clientKey, graphId, q),
    cacheduration: String(CB_NEW_CASE_SEARCH_CACHE_DURATION_SEC),
    cachegroup: CB_NEW_CASE_CACHE_GROUP,
  });
  return genericRecordsToClientSelectOptions(records);
}
